import React from "react";
import { ArrowRight, CheckCircle, Sparkles, Globe, Smartphone, MessageSquare, Image, Cpu, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import OurProcess from "@/components/ui/OurProcess";
import { mock } from "@/mock";

export default function GenerativeAiSolutions() {
  const navigate = useNavigate();
  const service = mock.services.find(s => s.id === "generative-ai");

  const genAiProcessSteps = [
    {
      number: "01",
      title: "Use Case Identification",
      description: "Discovering AI opportunities & requirements"
    },
    {
      number: "02", 
      title: "Model Selection & Fine-tuning",
      description: "Choosing & customizing AI models"
    },
    {
      number: "03",
      title: "Integration Development", 
      description: "Building workflows & system connections"
    },
    {
      number: "04",
      title: "Optimization & Scaling",
      description: "Performance tuning & deployment"
    }
  ];

  if (!service) return null;

  return (
    <div className="mx-auto max-w-[1200px] px-5 pt-2 pb-16">
      {/* Hero Section with Overlay Image */}
      <>
        <div className="relative mb-4 rounded-3xl overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="relative h-[500px] animate-in fade-in duration-1000">
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70 z-10"></div>
            <img 
              src="/generativeAI.png" 
              alt="Generative AI Solutions" 
              className="absolute inset-0 w-full h-full object-cover opacity-85"
            />
            {/* Animated Particles Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 animate-pulse z-[5]"></div>
            
            {/* Content Overlay */}
            <div className="relative z-20 h-full flex flex-col justify-center px-8 md:px-16">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-xs text-primary mb-6 w-fit animate-in slide-in-from-left-8 duration-700">
                <Sparkles className="h-4 w-4" />
                Generative AI Solutions
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-white md:text-6xl max-w-3xl animate-in slide-in-from-left-8 duration-700 delay-100">
                {service.title}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/90 animate-in slide-in-from-left-8 duration-700 delay-200">
                Transform your business with cutting-edge generative AI. We design and deploy custom solutions using LLMs, image generation, and more to automate, create, and innovate at scale.
              </p>
            </div>
          </div>
        </div>
      </>

      {/* Key Features */}
      <>
        <div className="mt-4 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">LLM Integration</h3>
              <p className="text-sm text-white/60">Advanced language models</p>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Image className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">AI Generation</h3>
              <p className="text-sm text-white/60">Image & content creation</p>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Cpu className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">Fine-Tuning</h3>
              <p className="text-sm text-white/60">Custom model training</p>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">Workflow Automation</h3>
              <p className="text-sm text-white/60">AI-powered processes</p>
            </CardContent>
          </Card>
        </div>
      </>

      <Separator className="my-16 bg-white/10" />
      
      {/* Our Process Section */}
      <>
        <OurProcess 
          steps={genAiProcessSteps}
          title="Our GenAI Development Process"
          subtitle="End-to-end generative AI solution development and deployment"
        />
      </>

      <Separator className="my-16 bg-white/10" />

      <div className="grid gap-12 lg:grid-cols-2">
        {/* Problems We Solve */}
        <>
          <Card className="border-white/10 bg-black/40 backdrop-blur-md">
            <CardHeader className="pb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-red-400" />
                Problems We Solve
              </h2>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {service.problems.map((problem, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/70">
                    <div className="h-2 w-2 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    {problem}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>

        {/* Deliverables */}
        <>
          <Card className="border-white/10 bg-black/40 backdrop-blur-md">
            <CardHeader className="pb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-green-400" />
                What You Get
              </h2>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {service.deliverables.map((deliverable, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/70">
                    <div className="h-2 w-2 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                    {deliverable}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      </div>

      {/* CTA */}
      <>
        <div className="mt-16 text-center">
          <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-8 backdrop-blur-md">
            <h3 className="text-2xl font-semibold text-white mb-4">
              Ready to Harness Generative AI?
            </h3>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Let's explore how AI can transform your workflows and unlock new possibilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                className="h-[52px] rounded-xl bg-primary px-6 text-black btn-glow hover:bg-primary/90"
              >
                Get a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate("/contact")}
                className="h-[52px] rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/10"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </>
    </div>
  );
}
